import type {
  Breadcrumb,
  ErrorEvent,
  EventHint,
  NodeOptions,
} from "@sentry/nextjs";
import * as Sentry from "@sentry/nextjs";

import { redactTokens, redactTokensDeep } from "./redact-tokens";

// @sentry/nextjs doesn't re-export `StreamedSpanJSON`.
type StreamedSpanJSON = Parameters<
  NonNullable<NodeOptions["beforeSendSpan"]>
>[0];

// Production builds replace server error messages with a generic "An error
// occurred in the Server Components render..." and keep only a `digest`. The
// server-side event for the same failure carries the same digest, so tagging
// it lets `digest:<value>` in Sentry link the opaque client error to the real
// one. Next.js control-flow errors have readable digests, e.g.
// `NEXT_HTTP_ERROR_FALLBACK;404` for notFound().
export function tagErrorDigest(event: ErrorEvent, hint: EventHint) {
  const error = hint.originalException;
  if (
    error instanceof Error &&
    "digest" in error &&
    typeof error.digest === "string"
  ) {
    event.tags = { ...event.tags, digest: error.digest };
  }
  return event;
}

// `local@domain.tld`, or its URL-encoded form `local%40domain.tld`.
const EMAIL_PATTERN = /[\w.%+-]+(?:@|%40)[\w.-]+\.[a-z]{2,}/gi;

export function redactEmails(value: string) {
  return value.replace(EMAIL_PATTERN, "[email]");
}

// Where Sentry's http and fetch instrumentation store outgoing request URLs:
// `url` on breadcrumb data, `url.*` on span attributes (breadcrumbs also
// carry `url.query`).
const URL_KEYS = ["url", "url.full", "url.path", "url.query"];

function redactUrlKeys(data: Record<string, unknown> | undefined) {
  if (!data) return;
  for (const key of URL_KEYS) {
    const value = data[key];
    if (typeof value === "string") data[key] = redactEmails(value);
  }
}

// Sender.net (lib/sender.ts) puts the subscriber email in the request path,
// and Sentry records outgoing URLs in breadcrumbs and spans. Only URLs and
// breadcrumb messages are scrubbed; `setUserToSentry` keeps the email on
// purpose.
export function redactBreadcrumbEmails(breadcrumb: Breadcrumb) {
  if (breadcrumb.message) breadcrumb.message = redactEmails(breadcrumb.message);
  redactUrlKeys(breadcrumb.data);
  return breadcrumb;
}

export function redactSpanEmails(span: StreamedSpanJSON) {
  span.name = redactEmails(span.name);
  redactUrlKeys(span.attributes);
  return span;
}

// Bearer-token URLs (see redact-tokens.ts) reach Sentry as the page or request
// URL, the Referer header, Next.js' request_path context, console messages and
// breadcrumbs, and span names and attributes. Stack frames are left alone so
// source maps still resolve. The typeof checks matter: Sentry copies
// error.message as is, so `value` can be a number or an object, and a throw
// here makes Sentry drop the event.
export function redactEventTokens(event: ErrorEvent) {
  if (typeof event.message === "string") {
    event.message = redactTokens(event.message);
  }
  if (typeof event.transaction === "string") {
    event.transaction = redactTokens(event.transaction);
  }
  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === "string") {
      exception.value = redactTokens(exception.value);
    }
  }
  event.request = redactTokensDeep(event.request);
  event.logentry = redactTokensDeep(event.logentry);
  event.contexts = redactTokensDeep(event.contexts);
  event.extra = redactTokensDeep(event.extra);
  event.tags = redactTokensDeep(event.tags);
  return event;
}

export function redactBreadcrumbTokens(breadcrumb: Breadcrumb) {
  if (typeof breadcrumb.message === "string") {
    breadcrumb.message = redactTokens(breadcrumb.message);
  }
  breadcrumb.data = redactTokensDeep(breadcrumb.data);
  return breadcrumb;
}

export function redactSpanTokens(span: StreamedSpanJSON) {
  if (typeof span.name === "string") span.name = redactTokens(span.name);
  span.attributes = redactTokensDeep(span.attributes);
  return span;
}

export function setUserToSentry({
  userId,
  email,
}: {
  userId?: string | null;
  email?: string;
}) {
  Sentry.setUser({ id: userId || undefined, email: email });
}
